import { useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { toasterApi } from '@terra/ui-design-system';
import type { WorkspaceSummary } from '../electron-shared/workspace/workspace-descriptor';

export function useRecentWorkspaces() {
  const t = useTranslations('proxy.workspaces');
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    const workspaceApi = window.electronAPI?.workspace;

    if (!workspaceApi) {
      setIsLoaded(true);

      return;
    }

    try {
      setWorkspaces(await workspaceApi.listRecent());
    } catch (error) {
      console.warn('Failed to load recent workspaces; showing none', error);
    } finally {
      setIsLoaded(true);
    }
  }, []);

  useEffect(() => {
    void refresh();

    return window.electronAPI?.workspace?.onChanged(() => void refresh());
  }, [refresh]);

  const forget = useCallback(
    async (workspaceId: string): Promise<void> => {
      const workspaceApi = window.electronAPI?.workspace;

      if (!workspaceApi) {
        return;
      }

      const result = await workspaceApi.forget(workspaceId);

      if (!result.success) {
        toasterApi.error({
          title: t('removeFailed'),
          description: result.error,
        });
      }

      await refresh();
    },
    [refresh, t],
  );

  return { workspaces, isLoaded, forget };
}
